import React, { useState, useEffect, useRef } from "react";
import { auth } from "../../config/firebase";
import { onAuthStateChanged, signInWithCustomToken } from "firebase/auth";
import ChatService from "../../services/FirebaseChatService";
import MainButton from "../../components/Elements/MainButton/MainButton";
import MainInput from "../../components/Elements/MainInput/MainInput";
import PageHeader from "../../components/Elements/PageHeader/PageHeader";
// import fileExportIcon from "../../../assets/file-export.png";
import "./SupportChats.css";

const SupportChats = () => {
  const [firebaseUser, setFirebaseUser] = useState(null);
  const [chats, setChats] = useState([]);
  const [selectedChat, setSelectedChat] = useState(null);
  const [messages, setMessages] = useState([]);
  const [replyText, setReplyText] = useState("");

  const messagesEndRef = useRef(null);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({
      behavior: "smooth",
      block: "end",
    });
  }, [messages, selectedChat]);

  useEffect(() => {
    const loginWithBackendToken = async () => {
      const token = localStorage.getItem("firebaseToken");

      if (!token) {
        console.groupEnd();
        return;
      }

      if (auth.currentUser) {
        console.groupEnd();
        return;
      }

      try {
        const cred = await signInWithCustomToken(auth, token);

        const freshTokenResult = await cred.user.getIdTokenResult(true);
      } catch (error) {
      } finally {
        console.groupEnd();
      }
    };

    loginWithBackendToken();
  }, []);

  useEffect(() => {
    const unsub = onAuthStateChanged(auth, async (user) => {
      // console.group("👤 onAuthStateChanged fired");

      if (user) {
        const token = await user.getIdTokenResult();
      } else {
        console.warn("No user detected. Firestore will deny all requests.");
      }
      setFirebaseUser(user);
    });
    return unsub;
  }, []);

  useEffect(() => {
    if (!firebaseUser) {
      return;
    }
    const unsub = ChatService.subscribeToAllChats(setChats);
    return unsub;
  }, [firebaseUser]);

  useEffect(() => {
    if (!selectedChat?.id || !firebaseUser) return;
    const unsub = ChatService.subscribeToChatMessages(selectedChat.id, setMessages);
    return unsub;
  }, [selectedChat, firebaseUser]);

  useEffect(() => {
    if (chats.length > 0 && !selectedChat) {
      // console.log("🎯 Auto-selecting first chat:", chats[0]);
      setSelectedChat(chats[0]);
    }
  }, [chats]);

  const handleSendReply = async () => {
    if (!replyText.trim() || !selectedChat || !firebaseUser) return;

    await ChatService.sendMessage(selectedChat.id, replyText.trim(), firebaseUser.email, "support");
    setReplyText("");
  };

  // if (!firebaseUser) {
  //   return (
  //     <BorderContainer>
  //       <div className="loading-state">
  //         <h3>Loading admin panel...</h3>
  //         <p>Status: Firebase loading...</p>
  //       </div>
  //     </BorderContainer>
  //   );
  // }

  const groupMessagesByDate = (messages) => {
    if (!messages.length) return [];

    const groups = [];
    let currentGroup = { date: messages[0].createdAt?.toDate(), messages: [messages[0]], isNewDay: true };
    const today = new Date();

    for (let i = 1; i < messages.length; i++) {
      const msgDate = messages[i].createdAt?.toDate();
      const prevDate = currentGroup.date;

      const isNewDay = !isSameDay(msgDate, prevDate);

      if (isNewDay) {
        groups.push(currentGroup);
        currentGroup = { date: msgDate, messages: [messages[i]], isNewDay: true };
      } else {
        currentGroup.messages.push(messages[i]);
      }
    }
    groups.push(currentGroup);
    return groups;
  };

  const formatChatListDate = (timestamp) => {
    if (!timestamp?.toDate) return "Never";

    const date = timestamp.toDate();
    const now = new Date();

    const sameDay =
      date.getDate() === now.getDate() &&
      date.getMonth() === now.getMonth() &&
      date.getFullYear() === now.getFullYear();

    if (sameDay) {
      return "Today";
    }

    const yesterday = new Date();
    yesterday.setDate(now.getDate() - 1);

    const isYesterdayDate =
      date.getDate() === yesterday.getDate() &&
      date.getMonth() === yesterday.getMonth() &&
      date.getFullYear() === yesterday.getFullYear();

    if (isYesterdayDate) {
      return "Yesterday";
    }

    return date.toLocaleDateString("en-GB", {
      day: "numeric",
      month: "short",
      year: "numeric",
    });
  };

  const isSameDay = (date1, date2) => {
    if (!date1 || !date2) return false;
    return date1.toDateString() === date2.toDateString();
  };

  // Formatuj datę
  const formatDate = (date) => {
    if (!date) return "Today";
    const msgDate = date; // Już jest Date!
    const today = new Date();

    if (isToday(msgDate)) return "Today";
    if (isYesterday(msgDate)) return "Yesterday";

    return msgDate.toLocaleDateString("en-GB", {
      day: "numeric",
      month: "short",
      year: "numeric",
    });
  };

  const isToday = (someDate) => {
    const today = new Date();
    const todayMidnight = new Date(today.getFullYear(), today.getMonth(), today.getDate());
    const dateMidnight = new Date(someDate.getFullYear(), someDate.getMonth(), someDate.getDate());
    return todayMidnight.getTime() === dateMidnight.getTime();
  };

  const isYesterday = (someDate) => {
    const yesterday = new Date();
    yesterday.setDate(yesterday.getDate() - 1);
    const yesterdayMidnight = new Date(yesterday.getFullYear(), yesterday.getMonth(), yesterday.getDate());
    const dateMidnight = new Date(someDate.getFullYear(), someDate.getMonth(), someDate.getDate());
    return yesterdayMidnight.getTime() === dateMidnight.getTime();
  };

  return (
    <main>
      <PageHeader
        title="Support Chats"
        description="View chat and message back users, to help them with their issues."
      />
      <div className="admin-help-chats">
        {/* ✅ SINGLE BorderContainer */}
        <div className="unified-chat-container">
          <div className="chats-split-layout">
            {/* LEFT: Chats List - Scrollable */}
            <div className="chats-list-panel">
              <div className="panel-header">
                <h2>Chats</h2>
              </div>
              <div className="chats-list">
                {chats.map((chat) => (
                  <div
                    key={chat.id}
                    className={`chat-item ${selectedChat?.id === chat.id ? "active" : ""}`}
                    onClick={() => setSelectedChat(chat)}
                  >
                    <div className="chat-user">{chat.email || chat.userId || "Unknown"}</div>
                    <div className="chat-preview">{chat.lastMessage?.slice(0, 50)} ...</div>
                    <div className="chat-time">{formatChatListDate(chat.lastMessageAt)}</div>
                  </div>
                ))}
              </div>
            </div>

            {/* ✅ MINIMAL DIVIDER */}
            <div className="vertical-divider" />

            {/* RIGHT: Chat Detail - Scrollable */}
            <div className="chat-detail-panel">
              {selectedChat ? (
                <>
                  <div className="panel-header">
                    <h3>Chat with {selectedChat.email}</h3>
                  </div>

                  <div className="messages-container">
                    {groupMessagesByDate(messages).map((group) => (
                      <div
                        key={group.date?.toISOString() || `group-${group.messages[0]?.id}`}
                        className="message-group"
                      >
                        {group.isNewDay && <div className="date-divider">{formatDate(group.date)}</div>}
                        {group.messages.map((msg) => (
                          <div
                            key={msg.id}
                            className={`message ${msg.sender === "support" ? "admin-message" : "user-message"}`}
                          >
                            <div className="message-bubble">
                              <div className="message-text">{msg.text}</div>
                              <div className="message-time">
                                {msg.createdAt?.toDate()
                                  ? new Date(msg.createdAt.toDate()).toLocaleTimeString("pl-PL", {
                                      hour: "2-digit",
                                      minute: "2-digit",
                                      hour12: false,
                                    })
                                  : ""}
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    ))}
                    <div ref={messagesEndRef} />
                  </div>

                  <div className="reply-section">
                    <MainInput
                      value={replyText}
                      onChange={(e) => setReplyText(e.target.value)}
                      placeholder="Type your reply..."
                      className={"reply-input"}
                    />
                    <MainButton onClick={handleSendReply} disabled={!replyText.trim()} className={"send-button"}>
                      Send Reply
                    </MainButton>
                  </div>
                </>
              ) : (
                <div className="no-chat-selected">
                  {chats.length === 0 ? <h3>Loading chats...</h3> : <h3>No chats yet</h3>}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </main>
  );
};

export default SupportChats;
